import React, { useCallback } from 'react';
import { ListActionSelect } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { CREDIT_CARDS_ICONS } from '../../../../../constants/credit-cards';
import type { CardType } from '../../../../../graphql/types';
import type { CreditCardOption } from '../../../Checkout.utils';
import type { useCheckoutFieldsValidation } from '../../../hooks';

export const CheckoutPaymentBlockCreditCardPicker = ({
  creditCard,
  creditCardOptions,
  onCreditCardSelected,
  onAddCardSelected,
  checkoutFieldsValidation,
  resetCheckoutFieldValidation,
}: CheckoutPaymentBlockCreditCardPickerProps) => {
  const { t } = useLocalizationContext();
  // Credit card from options by value.
  const selectedCard = useCardByValueOrFirst(creditCard, creditCardOptions);
  const selectedCardIcon = CREDIT_CARDS_ICONS[selectedCard?.cardType];

  // Automatically select first credit card if none are selected.
  React.useEffect(() => {
    if (creditCard !== 'ADD' || !selectedCard) return;
    onCreditCardSelected(selectedCard.value);
  }, [selectedCard, creditCard, onCreditCardSelected]);

  // Parent callback.
  const handleSelectedCardChange = useCallback(
    (cardId: string | number) => {
      resetCheckoutFieldValidation('billingAccountId');
      onCreditCardSelected(cardId as string);
      const isAddCardOptionSelected = cardId === 'ADD';

      if (isAddCardOptionSelected) onAddCardSelected();
    },
    [resetCheckoutFieldValidation, onCreditCardSelected, onAddCardSelected],
  );
  // ────────────────────────

  const isSelectCardFieldValid = checkoutFieldsValidation.billingAccountId;
  const selectCardFieldNotice = isSelectCardFieldValid
    ? undefined
    : t('checkout.required-field');

  // ────────────────────────
  return (
    <ListActionSelect
      title=""
      iconName={selectedCardIcon}
      value={selectedCard.value}
      onSelect={handleSelectedCardChange}
      options={creditCardOptions}
      notice={selectCardFieldNotice}
      noticePalette="caution"
    />
  );
};

// If we already have selected a credit card, use that one.
// Otherwise, use the second one, since the first is "Add card".
// If there are no available options, use the "Add card" option.
const useCardByValueOrFirst = (
  creditCard: string,
  creditCardOptions: readonly CreditCardOption[],
) => {
  const { t } = useLocalizationContext();
  const [selectedCard, setSelectedCard] = React.useState({
    value: 'ADD',
    label: t('checkout.add-card'),
    cardType: 'NEW' as CardType | 'NEW',
  });

  React.useEffect(() => {
    const cardToFind = creditCard === 'ADD' ? '' : creditCard;

    setSelectedCard(
      creditCardOptions.find(({ value }) => value === cardToFind) ??
        creditCardOptions[1] ??
        creditCardOptions[0],
    );
  }, [creditCardOptions, creditCard, setSelectedCard]);

  return selectedCard;
};

type CheckoutPaymentBlockCreditCardPickerProps = {
  readonly creditCard: string;
  readonly creditCardOptions: readonly CreditCardOption[];
  readonly onCreditCardSelected: (id: string) => void;
  readonly onAddCardSelected: () => void;
} & Pick<
  ReturnType<typeof useCheckoutFieldsValidation>,
  'resetCheckoutFieldValidation' | 'checkoutFieldsValidation'
>;
